<template>
  <div class="handout-html-preview" :class="{ 'is-embedded': embedded }">
    <div class="preview-pages" :style="pagesShellStyle">
      <RichEditor
        ref="previewEditor"
        preview-only
        handout-mode
        :handout-title="title"
        :value="previewEditorHtml"
        :preferred-content-width="effectiveContentWidth"
        :show-question-source="showQuestionSource"
        :show-question-difficulty="showQuestionDifficulty"
        :show-page-numbers="showPageNumbers"
        height="100%"
        @handout-layout="onHandoutLayout"
      />
    </div>
  </div>
</template>

<script>
import RichEditor from './RichEditor.vue'
import { extractHandoutPaginationPlan } from '../utils/handoutPagination'
import { ensureOptionLayoutsInHtml } from '../utils/handoutOptionLayout'

export default {
  name: 'HandoutHtmlPreview',
  components: { RichEditor },
  props: {
    title: { type: String, default: '' },
    contentHtml: { type: String, default: '' },
    showQuestionSource: { type: Boolean, default: true },
    showQuestionDifficulty: { type: Boolean, default: true },
    embedded: { type: Boolean, default: false },
    showPageNumbers: { type: Boolean, default: true },
    preferredContentWidth: { type: Number, default: 0 }
  },
  computed: {
    previewEditorHtml() {
      const html = this.contentHtml || ''
      if (!html.trim()) return ''
      try {
        return ensureOptionLayoutsInHtml(html)
      } catch (e) {
        return html
      }
    },
    effectiveContentWidth() {
      const preferred = Number(this.preferredContentWidth) || 0
      if (preferred > 0) return preferred
      const plan = extractHandoutPaginationPlan(this.contentHtml || '')
      if (plan && plan.contentWidth > 0) return plan.contentWidth
      return 760
    },
    pagesShellStyle() {
      return {
        width: `${this.effectiveContentWidth}px`,
        maxWidth: '100%',
        margin: this.embedded ? '0' : '0 auto'
      }
    }
  },
  watch: {
    contentHtml() {
      this.syncPreviewEditorLayout()
    }
  },
  mounted() {
    this.syncPreviewEditorLayout()
  },
  methods: {
    syncPreviewEditorLayout() {
      this.$nextTick(() => {
        const editor = this.$refs.previewEditor
        if (!editor) return
        if (typeof editor.syncEditorHtml === 'function') {
          editor.syncEditorHtml(this.previewEditorHtml)
        }
        if (typeof editor.decorateHandoutEditor === 'function') {
          editor.decorateHandoutEditor(true)
        }
        if (typeof editor.flushHandoutEditorLayout === 'function') {
          editor.flushHandoutEditorLayout(true)
        }
      })
    },
    onHandoutLayout(payload) {
      this.$emit('layout', payload || {})
    },
    getExportElement() {
      const editor = this.$refs.previewEditor
      if (!editor || !editor.$el) return null
      return editor.$el.querySelector('.editor-scroll') || editor.$el
    },
    async exportToPdf(options = {}) {
      const editor = this.$refs.previewEditor
      if (editor && typeof editor.decorateHandoutEditor === 'function') {
        editor.decorateHandoutEditor()
      }
      if (editor && typeof editor.flushHandoutEditorLayout === 'function') {
        editor.flushHandoutEditorLayout()
      }
      if (editor && typeof editor.syncHandoutTitleBannerLayout === 'function') {
        editor.syncHandoutTitleBannerLayout()
      }
      await this.$nextTick()
      await new Promise((resolve) => requestAnimationFrame(() => requestAnimationFrame(resolve)))

      const { exportHandoutPreviewToPdf } = await import('../utils/handoutExportPdf')
      const root = this.getExportElement()
      let contentHtml = options.contentHtml != null ? options.contentHtml : this.contentHtml
      if (editor && typeof editor.buildHandoutPaginationSnapshot === 'function') {
        try {
          contentHtml = editor.buildHandoutPaginationSnapshot()
        } catch (e) {}
      }
      await exportHandoutPreviewToPdf(root, {
        title: options.title != null ? options.title : this.title,
        contentHtml,
        versionLabel: options.versionLabel || '学生版',
        syncTitleBanner: editor && typeof editor.syncHandoutTitleBannerLayout === 'function'
          ? () => editor.syncHandoutTitleBannerLayout()
          : null
      })
    }
  }
}
</script>

<style scoped>
.handout-html-preview {
  width: 100%;
  height: 100%;
  overflow: auto;
  background: #eef1f5;
  padding: 24px 16px 32px;
  box-sizing: border-box;
}

.handout-html-preview.is-embedded {
  height: auto;
  overflow: visible;
  background: transparent;
  padding: 0;
}

.preview-pages {
  margin: 0 auto;
}

.handout-html-preview.is-embedded .preview-pages {
  margin: 0;
}

.handout-html-preview ::v-deep .rich-editor.preview-only {
  border: none;
  border-radius: 0;
  height: auto;
  overflow: visible;
  background: transparent;
}

.handout-html-preview ::v-deep .rich-editor.preview-only .editor-body {
  flex: none;
  overflow: visible;
  min-height: 0;
}

.handout-html-preview ::v-deep .rich-editor.preview-only .editor-scroll {
  overflow: visible;
}

.handout-html-preview ::v-deep .rich-editor.preview-only .w-e-text-container {
  height: auto !important;
}

.handout-html-preview ::v-deep .rich-editor.preview-only .w-e-scroll {
  height: auto !important;
  overflow: visible !important;
}
</style>
